// Types and merge rules shared by the browser and the Worker.
//
// Everything a person accumulates is either a *progress record* (one per puzzle) or a *day row* (thinking time
// per calendar day per device). Both merge without conflicts: every field only ever moves "forward", so the
// order in which devices sync never matters.

export type Family = "cut" | "stars" | "coin";
export const FAMILIES: readonly Family[] = ["cut", "stars", "coin"];

/** The three thinking muscles; each puzzle family trains one. */
export const MUSCLE: Record<Family, "See" | "Bend" | "Plan"> = { cut: "See", stars: "Bend", coin: "Plan" };

export type Status = "open" | "revealed" | "solved";
const RANK: Record<Status, number> = { open: 0, revealed: 1, solved: 2 };

export interface ProgressRecord {
  /** e.g. "cut-r1" (a rung of the ladder) or "stars-d20260930-2" (the daily puzzle for that date and difficulty) */
  id: string;
  family: Family;
  status: Status;
  /** puzzle-specific in-progress state; small JSON */
  state: unknown;
  /** active thinking seconds spent on this puzzle */
  seconds: number;
  hints: number;
  /** short human summary of how it went, e.g. "4 lines" or "2 weighings" */
  result: string;
  solvedAt: number | null;
  /** "in your own words" reflection */
  note: string;
  noteAt: number;
  updatedAt: number;
}

export interface DayRow {
  day: string; // YYYY-MM-DD in the person's own time zone
  device: string;
  cut: number; // thinking seconds per family
  stars: number;
  coin: number;
  solved: number;
}

export interface Settings {
  goalMinutes: number;
  theme: "auto" | "light" | "dark";
  sound: boolean;
}

export const DEFAULT_SETTINGS: Settings = { goalMinutes: 10, theme: "auto", sound: false };

export interface UserInfo {
  id: string;
  email: string;
  name: string;
  settings: Partial<Settings>;
  createdAt: number;
}

// ---- puzzle ids ----

export type ParsedId =
  | { family: Family; kind: "rung"; rung: number }
  | { family: Family; kind: "daily"; date: string; difficulty: number };

const ID_RE = /^(cut|stars|coin)-(?:r([1-9])|d(\d{8})-([1-9]))$/;

export function parsePuzzleId(id: string): ParsedId | null {
  const m = ID_RE.exec(id);
  if (!m) return null;
  const family = m[1] as Family;
  if (m[2]) return { family, kind: "rung", rung: Number(m[2]) };
  return { family, kind: "daily", date: m[3], difficulty: Number(m[4]) };
}

export function rungId(family: Family, rung: number): string {
  return `${family}-r${rung}`;
}

export function dailyId(family: Family, dayKey: string, difficulty: number): string {
  return `${family}-d${dayKey.replace(/-/g, "")}-${difficulty}`;
}

/** "2026-09-30" ⇄ "20260930" */
export function isDayKey(s: unknown): s is string {
  return typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

// ---- merge rules ----

function cmp(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** total order on (how far along, when it was last touched, what the board looks like, how it went) */
function leadOrder(a: ProgressRecord, b: ProgressRecord): number {
  return (
    RANK[a.status] - RANK[b.status] ||
    a.updatedAt - b.updatedAt ||
    cmp(JSON.stringify(a.state ?? null), JSON.stringify(b.state ?? null)) ||
    cmp(a.result, b.result)
  );
}

export function emptyProgress(id: string, family: Family): ProgressRecord {
  return { id, family, status: "open", state: null, seconds: 0, hints: 0, result: "", solvedAt: null, note: "", noteAt: 0, updatedAt: 0 };
}

/**
 * Combine two versions of the same puzzle's record. Commutative, associative and idempotent, so devices can sync
 * in any order and always agree:
 *  - the record that is further along (solved > revealed > open, then most recently touched) supplies the
 *    status, board state and result,
 *  - counters take the maximum and the solve time the earliest,
 *  - the reflection note comes from whichever was written last.
 */
export function mergeProgress(a: ProgressRecord | null | undefined, b: ProgressRecord | null | undefined): ProgressRecord {
  if (!a && !b) throw new Error("mergeProgress needs at least one record");
  if (!a) return b!;
  if (!b) return a;
  const lead = leadOrder(a, b) >= 0 ? a : b;
  const noteFrom = a.noteAt !== b.noteAt ? (a.noteAt > b.noteAt ? a : b) : a.note >= b.note ? a : b;
  const solvedAts = [a.solvedAt, b.solvedAt].filter((v): v is number => typeof v === "number");
  return {
    id: lead.id,
    family: lead.family,
    status: lead.status,
    state: lead.state,
    seconds: Math.max(a.seconds, b.seconds),
    hints: Math.max(a.hints, b.hints),
    result: lead.result,
    solvedAt: solvedAts.length ? Math.min(...solvedAts) : null,
    note: noteFrom.note,
    noteAt: noteFrom.noteAt,
    updatedAt: lead.updatedAt
  };
}

export function sameProgress(a: ProgressRecord, b: ProgressRecord): boolean {
  return (
    a.id === b.id && a.family === b.family && a.status === b.status && a.seconds === b.seconds && a.hints === b.hints &&
    a.result === b.result && a.solvedAt === b.solvedAt && a.note === b.note && a.noteAt === b.noteAt && a.updatedAt === b.updatedAt &&
    JSON.stringify(a.state ?? null) === JSON.stringify(b.state ?? null)
  );
}

export function sameDay(a: DayRow, b: DayRow): boolean {
  return a.cut === b.cut && a.stars === b.stars && a.coin === b.coin && a.solved === b.solved;
}

export function mergeDay(a: DayRow, b: DayRow): DayRow {
  return {
    day: a.day,
    device: a.device,
    cut: Math.max(a.cut, b.cut),
    stars: Math.max(a.stars, b.stars),
    coin: Math.max(a.coin, b.coin),
    solved: Math.max(a.solved, b.solved)
  };
}

export function daySeconds(r: Pick<DayRow, "cut" | "stars" | "coin">): number {
  return r.cut + r.stars + r.coin;
}

// ---- limits (enforced by the Worker, respected by the client) ----

export const LIMITS = {
  stateBytes: 12_000,
  noteChars: 1200,
  resultChars: 60,
  maxSecondsPerDay: 24 * 3600,
  maxProgressRows: 6000,
  maxDayRows: 6000,
  syncBodyBytes: 400_000
} as const;
